import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CheckCircle2, ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { ASSESSMENT_TYPES, ASSESSMENT_TYPE_LABEL } from "@/lib/assessmentTypes";

const PROFILES = [
  { code: "BASE", label: "BASE", desc: "Profilo essenziale per micro-imprese e PMI" },
  { code: "STANDARD", label: "STANDARD", desc: "Standard VSME (PMI strutturate)" },
  { code: "ESTESO", label: "ESTESO", desc: "Profilo completo ESRS-aligned" },
];

export default function NewAssessment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedOrg = searchParams.get("org") || "";
  const [step, setStep] = useState(0);
  const [orgs, setOrgs] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [data, setData] = useState({
    organization_id: preselectedOrg,
    profile_level: "BASE",
    cluster_code: "",
    year: new Date().getFullYear(),
    assessment_type: "prima_emissione",
    reference_certificate_code: "",
  });
  const [certs, setCerts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [o, c, a] = await Promise.all([api.get("/organizations"), api.get("/pillar-weights"), api.get("/assessments")]);
      setOrgs(o.data);
      setClusters(c.data);
      setCerts(a.data.filter((x) => x.certificate_code));
    })();
  }, []);

  const steps = ["Azienda", "Profilo", "Cluster settoriale", "Anno"];

  function next() { setStep((s) => Math.min(s + 1, steps.length - 1)); }
  function back() { setStep((s) => Math.max(s - 1, 0)); }

  async function submit() {
    setSubmitting(true);
    try {
      const payload = { ...data, reference_certificate_code: data.assessment_type === "prima_emissione" ? null : data.reference_certificate_code };
      const { data: created } = await api.post("/assessments", payload);
      toast.success("Assessment creato");
      navigate(`/assessment/${created.id}/compila`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  }

  const canNext = (
    (step === 0 && data.organization_id) ||
    (step === 1 && data.profile_level) ||
    (step === 2 && data.cluster_code) ||
    (step === 3 && data.year && (data.assessment_type === "prima_emissione" || data.reference_certificate_code))
  );
  const orgCerts = certs.filter((x) => x.organization_id === data.organization_id);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Nuovo Assessment</div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Configura un nuovo assessment ESG</h1>
        <p className="text-slate-500 mt-1">Segui i passaggi per creare un nuovo questionario.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center mb-8 gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 ${i <= step ? "text-viva-700" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                i < step ? "bg-viva-500 border-viva-500 text-white" : i === step ? "border-viva-500 text-viva-700 bg-viva-50" : "border-slate-200"
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider hidden sm:block">{s}</div>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-viva-300" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <Card className="p-8 rounded-2xl border-slate-100 shadow-soft">
        {step === 0 && (
          <div>
            <h3 className="font-heading text-xl font-semibold mb-2">Seleziona l'azienda</h3>
            <p className="text-slate-500 text-sm mb-6">Scegli l'organizzazione da valutare.</p>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Azienda</Label>
            <Select value={data.organization_id} onValueChange={(v) => setData({ ...data, organization_id: v })}>
              <SelectTrigger data-testid="wizard-org-select" className="mt-1.5 h-11 rounded-lg">
                <SelectValue placeholder="Scegli un'azienda..." />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.ragione_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
            {orgs.length === 0 && (
              <p className="text-sm text-amber-600 mt-3">Nessuna azienda registrata. Crea prima un'azienda nell'anagrafica.</p>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 className="font-heading text-xl font-semibold mb-2">Profilo di reporting</h3>
            <p className="text-slate-500 text-sm mb-6">Determina il numero e la complessità degli indicatori.</p>
            <div className="grid gap-3">
              {PROFILES.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  data-testid={`wizard-profile-${p.code}`}
                  onClick={() => setData({ ...data, profile_level: p.code })}
                  className={`text-left p-5 rounded-xl border-2 transition ${
                    data.profile_level === p.code
                      ? "border-viva-500 bg-viva-50/50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold text-lg text-slate-900">{p.label}</div>
                    {data.profile_level === p.code && <CheckCircle2 className="w-5 h-5 text-viva-500" />}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{p.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="font-heading text-xl font-semibold mb-2">Cluster settoriale</h3>
            <p className="text-slate-500 text-sm mb-6">Determina i pesi relativi di E, S, G.</p>
            <Select value={data.cluster_code} onValueChange={(v) => setData({ ...data, cluster_code: v })}>
              <SelectTrigger data-testid="wizard-cluster-select" className="h-11 rounded-lg">
                <SelectValue placeholder="Scegli un cluster..." />
              </SelectTrigger>
              <SelectContent>
                {clusters.map((c) => (
                  <SelectItem key={c.cluster_code} value={c.cluster_code}>
                    {c.cluster_code} — {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data.cluster_code && (() => {
              const c = clusters.find((x) => x.cluster_code === data.cluster_code);
              return c && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <PillarWeightCard pillar="E" label="Environmental" weight={c.weight_e} color="viva" />
                  <PillarWeightCard pillar="S" label="Social" weight={c.weight_s} color="amber" />
                  <PillarWeightCard pillar="G" label="Governance" weight={c.weight_g} color="indigo" />
                </div>
              );
            })()}
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="font-heading text-xl font-semibold mb-2">Anno di riferimento</h3>
            <p className="text-slate-500 text-sm mb-6">L'anno fiscale o solare oggetto dell'assessment.</p>
            <Input
              type="number"
              value={data.year}
              onChange={(e) => setData({ ...data, year: Number(e.target.value) })}
              data-testid="wizard-year-input"
              className="h-11 rounded-lg w-40"
            />
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo assessment</Label>
                <Select value={data.assessment_type} onValueChange={(v) => setData({ ...data, assessment_type: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-1.5" data-testid="wizard-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSESSMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {data.assessment_type !== "prima_emissione" && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Certificato di riferimento *</Label>
                  <Select value={data.reference_certificate_code} onValueChange={(v) => setData({ ...data, reference_certificate_code: v })}>
                    <SelectTrigger className="h-11 rounded-lg mt-1.5" data-testid="wizard-ref-cert-select"><SelectValue placeholder={orgCerts.length ? "Scegli certificato..." : "Nessun certificato per questa azienda"} /></SelectTrigger>
                    <SelectContent>{orgCerts.map((c) => <SelectItem key={c.id} value={c.certificate_code}>{c.certificate_code} · {c.year}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="mt-8 p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Riepilogo</div>
              <div className="text-sm text-slate-700 space-y-1">
                <div><span className="font-medium">Azienda:</span> {orgs.find((o) => o.id === data.organization_id)?.ragione_sociale}</div>
                <div><span className="font-medium">Profilo:</span> {data.profile_level}</div>
                <div><span className="font-medium">Cluster:</span> {data.cluster_code}</div>
                <div><span className="font-medium">Anno:</span> {data.year}</div>
                <div><span className="font-medium">Tipo:</span> {ASSESSMENT_TYPE_LABEL[data.assessment_type]}{data.reference_certificate_code && data.assessment_type !== "prima_emissione" ? ` · rif. ${data.reference_certificate_code}` : ""}</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="wizard-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
          </Button>
          {step < steps.length - 1 ? (
            <Button
              onClick={next}
              disabled={!canNext}
              data-testid="wizard-next-button"
              className="bg-viva-500 hover:bg-viva-600 text-white"
            >
              Avanti <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting || !canNext}
              data-testid="wizard-submit-button"
              className="bg-viva-500 hover:bg-viva-600 text-white"
            >
              <ClipboardList className="w-4 h-4 mr-2" /> Crea assessment
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function PillarWeightCard({ pillar, label, weight, color }) {
  const colors = {
    viva: "bg-viva-50 text-viva-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
  };
  return (
    <div className={`p-4 rounded-xl ${colors[color]}`}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-70">{pillar} · {label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{(weight * 100).toFixed(0)}%</div>
    </div>
  );
}
